import { AnyAri } from '@atlassian/ari/any-ari';
import { getConfig } from '../../../config';
import { getGraphQLEndpointUrl, JSON_CONTENT_TYPE } from '../../state/graphqlEndpoints';
import { buildSiteAri } from '../labs/home/utils/siteUtils';
import {
	mergeInferenceRecords,
	parseInferenceQueryResponse,
	type InferenceQueryResponse,
	type InferenceRecord,
	type InferenceResultsPage,
	type InferenceResultType,
} from './inference-results-data';
import { matchesExplorerLocation, type ExplorerLocation } from './explorer-location';
import { inferenceDetailQuery } from './inference-detail-query';
import { emptyGraphClawOptions } from './types';
import type {
	ExternallyManagedInferenceApp,
	GraphClawApiVersion,
	GraphClawApprovedModelLookupResponse,
	GraphClawErrorKind,
	GraphClawInferDefinition,
	GraphClawInferenceAppPermissions,
	GraphClawInferenceQualityMetricValues,
	GraphClawInferenceQualityMetrics,
	GraphClawOption,
	GraphClawOptions,
	GraphClawSourceOption,
	GraphClawStoredDecisionMetadata,
	GraphClawStoredResult,
	InferenceApp,
	InferenceAppEditValues,
	InferenceAppFormValues,
	InferenceAppId,
} from './types';

const INFERENCE_APP_PERMISSIONS_QUERY = `
	query GraphClawInferenceAppPermissions($cloudId: ID!) {
		graphInference_inferenceAppPermissions(cloudId: $cloudId) {
			canView
			canManage
			canRunInference
		}
	}
`;

const INFERENCE_QUALITY_METRIC_FIELDS = `
	taskRuns
	tasksSucceeded
	tasksFailed
	successRate
	sourceCountReportedRuns
	sourceResultCountSum
	inputEmptyRuns
	inputEmptyRate
	resultsGeneratedSum
	outputEmptyRuns
	outputEmptyRate
	claimMetricsReportedRuns
	claimsGeneratedSum
	claimsWithEvidenceSum
	claimsWithoutEvidenceSum
	evidenceCoverageRate
	schemaCheckedRuns
	schemaValidRuns
	schemaInvalidRuns
	schemaValidRate
	tokenUsageReportedRuns
	inputTokensSum
	outputTokensSum
	totalTokensSum
	thoughtTokensSum
	cachedReadTokensSum
	cachedWriteTokensSum
	contextTokensSum
	taskLatencyP50Ms
	taskLatencyP95Ms
	responseSizeP50Bytes
	responseSizeP95Bytes
	confidenceP50
	confidenceP95
`;

const INFERENCE_QUALITY_METRICS_QUERY = `
	query GraphClawInferenceQualityMetrics($input: GraphInferenceInferenceQualityMetricsInput!) {
		graphInference_inferenceQualityMetrics(input: $input) {
			apiVersion
			windowStart
			windowEnd
			resolution
			dataFreshThrough
			summary {
				${INFERENCE_QUALITY_METRIC_FIELDS}
			}
			buckets {
				bucketStart
				finalized
				dataFreshThrough
				metrics {
					${INFERENCE_QUALITY_METRIC_FIELDS}
				}
			}
		}
	}
`;

const INFERENCE_APP_STORED_RESULTS_QUERY = `
	query GraphClawInferenceAppStoredResults($input: GraphInferenceInferenceAppStoredResultsInput!) {
		graphInference_inferenceAppStoredResults(input: $input) {
			inferenceNode {
				ari
				title
				description
				url
			}
			linksEntities {
				ari
				url
			}
			anchor {
				ari
				title
				description
				url
			}
		}
	}
`;

const INFERRED_DECISION_METADATA_QUERY = `
	query GraphClawHydrateInferredDecisionMetadata($ids: [ID!]!) {
		graphStoreV2 @optIn(to: "GraphStoreV2") {
			nodes(ids: $ids) {
				id
				metadata {
					__typename
					... on GraphStoreInferredDecisionNodeMetadata {
						kind
						subtype
						confidenceScore
						createdAt
						decidedBy
					}
				}
			}
		}
	}
`;

const INFERENCE_NODE_DATA_FIELDS = `
	__typename
	... on Node {
		id
	}
	... on AtlassianAccountUser {
		id
		name
		picture
		canonicalAccountId
	}
	... on ConfluencePage {
		id
		title
		links {
			base
			webUi
		}
	}
	... on JiraIssue {
		id
		key
		webUrl
	}
	... on JiraProject {
		id
		key
		name
		description
		webUrl
	}
	... on LoomVideo {
		id
		name
		loomUrl: url
	}
	... on TownsquareProject {
		id
		key
		name
		townsquareProjectUrl: url
	}
	... on TownsquareGoal {
		id
		key
		name
		townsquareGoalUrl: url
	}
	... on GraphStoreNodeResponse {
		id
		nodeType
		metadata {
			__typename
			... on GraphStoreInferredProjectNodeMetadata {
				confidenceScore
				createdAt
				lastActivityDate
				lastUpdated
				startDate
				summary
				title
			}
			... on GraphStoreInferredDecisionNodeMetadata {
				confidenceScore
				createdAt
				decidedBy
				kind
				lastUpdated
				subtype
				summary
				title
				provenance {
					appId
					appVersion
					executionMode
					modelProvider
					sourceVersion
				}
			}
			... on GraphStoreInferredTeamNodeMetadata {
				createdAt
				lastUpdated
			}
		}
	}
`;

const INFERENCE_RESULTS_CONNECTION_FIELDS = `
	edges {
		node {
			columns {
				key
				value {
					... on GraphStoreCypherQueryV2AriNode {
						id
						data {
							${INFERENCE_NODE_DATA_FIELDS}
						}
					}
					... on GraphStoreCypherQueryV2NodeList {
						nodes {
							id
							data {
								${INFERENCE_NODE_DATA_FIELDS}
							}
						}
					}
					... on GraphStoreCypherQueryV2StringObject {
						stringValue: value
					}
					... on GraphStoreCypherQueryV2IntObject {
						intValue: value
					}
				}
			}
		}
	}
`;

const INFERENCE_RESULTS_QUERY = `
	query GraphClawInferenceResults($cypherQuery: String!, $params: JSON) {
		graphStore @optIn(to: ["GraphStore", "GraphStoreCypherQueryV2"]) {
			cypherQueryV2(query: $cypherQuery, params: $params, version: V3, first: 1000) {
				${INFERENCE_RESULTS_CONNECTION_FIELDS}
			}
		}
	}
`;

const ALL_INFERENCE_RESULTS_QUERY = `
	query GraphClawAllInferenceResults(
		$projectQuery: String!
		$decisionQuery: String!
		$teamQuery: String!
		$artifactQuery: String!
	) {
		graphStore @optIn(to: ["GraphStore", "GraphStoreCypherQueryV2"]) {
			projects: cypherQueryV2(query: $projectQuery, version: V3, first: 1000) {
				${INFERENCE_RESULTS_CONNECTION_FIELDS}
			}
			decisions: cypherQueryV2(query: $decisionQuery, version: V3, first: 1000) {
				${INFERENCE_RESULTS_CONNECTION_FIELDS}
			}
			teams: cypherQueryV2(query: $teamQuery, version: V3, first: 1000) {
				${INFERENCE_RESULTS_CONNECTION_FIELDS}
			}
			artifacts: cypherQueryV2(query: $artifactQuery, version: V3, first: 1000) {
				${INFERENCE_RESULTS_CONNECTION_FIELDS}
			}
		}
	}
`;

const GRAPH_STORE_NODE_BATCH_SIZE = 25;
const GRAPH_STORE_HYDRATION_CONCURRENCY = 4;
const METADATA_HYDRATION_TIMEOUT_MS = 1_000;

export const INFERENCE_RESULTS_PAGE_SIZE = 50;

const getInferredProjectWorkspaceFilter = (): string => {
	// Temporary Hello migration workaround: filter before paging so stale workspace
	// records cannot fill the first page with projects whose metadata cannot hydrate.
	return getConfig().defaultSiteCloudId === 'a436116f-02ce-4520-8fbb-7301462a1674'
		? "AND ip.workspace = 'ari:cloud:graph-store::workspace/77c3bd3b-6e85-4a8f-8f03-800307bb8ed5'"
		: '';
};

const INFERENCE_CYPHER_QUERIES = {
	project: (offset: number, limit = INFERENCE_RESULTS_PAGE_SIZE) => `
		MATCH (ip:InferredProject)
		WHERE ip.status = 'active'
		${getInferredProjectWorkspaceFilter()}
		WITH ip
		ORDER BY ip.lastUpdated DESC
		SKIP ${offset}
		LIMIT ${limit}
		OPTIONAL MATCH (ip)-[:inferred_project_links_entity]->(referenceEntity)
		RETURN DISTINCT ip, referenceEntity
	`,
	decision: (offset: number, limit = INFERENCE_RESULTS_PAGE_SIZE) => `
		MATCH (ip:InferredDecision)
		WITH ip
		ORDER BY ip.lastUpdated DESC
		SKIP ${offset}
		LIMIT ${limit}
		OPTIONAL MATCH (ip)-[:inferred_decision_links_entity]->(referenceEntity)
		RETURN DISTINCT ip, referenceEntity
	`,
	team: (offset: number, limit = INFERENCE_RESULTS_PAGE_SIZE) => `
		MATCH (it:InferredTeam)-[:inferred_team_links_entity]->(jp:JiraProject)
		WITH it, collect(DISTINCT jp) AS jp
		ORDER BY it.lastUpdated DESC
		SKIP ${offset}
		LIMIT ${limit}
		OPTIONAL MATCH (it)-[:inferred_team_collaborates_on_inferred_project]->(ip:InferredProject {status: 'active'})
		RETURN it, jp, collect(DISTINCT ip) AS ip
	`,
	artifact: (offset: number, limit = INFERENCE_RESULTS_PAGE_SIZE) => `
		MATCH (project)-[relationship:atlassian_project_has_related_entity|inferred_project_has_related_entity]->(artifact)
		RETURN DISTINCT project, artifact,
			relationship.relevance AS relevance,
			relationship.provenance.appId AS appId,
			relationship.provenance.appVersion AS appVersion
		ORDER BY artifact.lastUpdated DESC, project.lastUpdated DESC
		SKIP ${offset}
		LIMIT ${limit}
	`,
} satisfies Record<Exclude<InferenceResultType, 'all'>, (offset: number, limit?: number) => string>;

const EXPANDED_INFERENCE_CYPHER_QUERIES = {
	project: (offset: number, limit = INFERENCE_RESULTS_PAGE_SIZE) => `
		MATCH (ip:InferredProject)
		WHERE ip.status = 'active'
		${getInferredProjectWorkspaceFilter()}
		WITH ip
		ORDER BY ip.lastUpdated DESC
		SKIP ${offset}
		LIMIT ${limit}
		OPTIONAL MATCH (ip)-[:inferred_project_links_entity]->(linkedEntity)
		WITH ip, collect(DISTINCT linkedEntity) AS linkedEntities
		OPTIONAL MATCH (inferredTeam:InferredTeam)-[:inferred_team_collaborates_on_inferred_project]->(ip)
		OPTIONAL MATCH (inferredTeam)-[:inferred_team_links_entity]->(inferredTeamBacking)
		WITH ip, linkedEntities, inferredTeam, inferredTeamBacking
		OPTIONAL MATCH (owner:IdentityUser)-[:atlassian_user_owns_inferred_project]->(ip)
		WITH ip, linkedEntities, inferredTeam, inferredTeamBacking, collect(DISTINCT owner) AS owners
		OPTIONAL MATCH (collaborator:IdentityUser)-[:atlassian_user_collaborates_on_inferred_project]->(ip)
		RETURN ip, linkedEntities, inferredTeam, inferredTeamBacking, owners, collect(DISTINCT collaborator) AS collaborators
	`,
	// Temporary mitigation: InferredProject impact permission checks fail in Identity.
	// Retain Townsquare project/goal impacts and decisions without matching impacts.
	decision: (offset: number, limit = INFERENCE_RESULTS_PAGE_SIZE) => `
		MATCH (ip:InferredDecision)
		WITH ip
		ORDER BY ip.lastUpdated DESC
		SKIP ${offset}
		LIMIT ${limit}
		OPTIONAL MATCH (ip)-[:inferred_decision_links_entity]->(linkedEntity)
		WITH ip, collect(DISTINCT linkedEntity) AS linkedEntities
		OPTIONAL MATCH (ip)-[:inferred_decision_impacts_entity]->(impactedProject:TownsquareProject)
		WITH ip, linkedEntities, collect(DISTINCT impactedProject) AS impactedProjects
		OPTIONAL MATCH (ip)-[:inferred_decision_impacts_entity]->(impactedGoal:TownsquareGoal)
		WITH ip, linkedEntities, impactedProjects, collect(DISTINCT impactedGoal) AS impactedGoals
		RETURN ip, linkedEntities, impactedProjects + impactedGoals AS impactedEntities
	`,
	team: INFERENCE_CYPHER_QUERIES.team,
} satisfies Record<
	Exclude<InferenceResultType, 'all' | 'artifact'>,
	(offset: number, limit?: number) => string
>;

const GRAPHCLAW_OPTIONS_QUERY = `
	query GraphClawOptions {
		products: graphInference_graphclawProductOptions {
			id
			name
			enabled
		}
		inferenceTargets: graphInference_graphclawInferenceTargets {
			id
			name
			enabled
		}
		inferenceTypes: graphInference_graphclawInferenceType {
			id
			name
			enabled
		}
	}
`;

const GRAPHCLAW_OPTIONS_V3_QUERY = `
	query GraphClawOptionsV3($source: String) {
		products: graphInference_graphclawProductOptionsV2 {
			id
			name
			enabled
			matchers {
				id
				name
				enabled
			}
		}
		inferenceTargets: graphInference_graphclawInferenceTargetsV2 {
			id
			name
			enabled
		}
		inferenceTypes: graphInference_graphclawInferV3(source: $source) {
			id
			name
			enabled
			resultKind
			targetRequirement
			targetUsage
			compatibleTargets
			compatibleSources
			supportedDestinations
			graphAvailable
			graphSources
		}
	}
`;

const INFERENCE_MODELS_QUERY = `
	query GraphClawInferenceModels($input: GraphInferenceInferenceModelsInput!) {
		graphInference_inferenceModels(input: $input) {
			useCaseId
			infer
			models {
				id
				label
			}
			runtimeSelectionSupported
		}
	}
`;

const INFERENCE_APP_LIST_QUERY = `
	query GraphClawInferenceAppList {
		graphInference_inferenceAppList {
			id
			name
			product
			inferenceTarget
			inferenceType
			enabled
			realtime
		}
	}
`;

const getInferenceAppV3Fields = (includePromptOverride: boolean): string => `
			id
			owner
			name
			source
			matchers
			forTargets
			infer
			destination
			aiUseCaseId
			modelId
			reasoningLevel
			enabled
			realtime
			available
			${includePromptOverride ? 'promptOverride' : ''}
`;

const getInferenceAppListV3Query = (includePromptOverride: boolean): string => `
	query GraphClawInferenceAppListV3 {
		graphInference_inferenceAppListV3 {
			${getInferenceAppV3Fields(includePromptOverride)}
		}
	}
`;

const EXTERNALLY_MANAGED_APP_LIST_QUERY = `
	query GraphClawExternallyManagedAppList {
		graphInference_externallyManagedAppList {
			id
			owner
			name
			source
			matchers
			forTargets
			infer
		}
	}
`;

const INFERENCE_APP_ADD_MUTATION = `
	mutation GraphClawInferenceAppAdd($input: GraphInferenceInferenceAppAddInput!) {
		graphInference_inferenceAppAdd(input: $input) {
			success
			errors {
				code
				message
			}
			app {
				id
				name
				product
				inferenceTarget
				inferenceType
				enabled
				realtime
			}
		}
	}
`;

const getInferenceAppAddV3Mutation = (includePromptOverride: boolean): string => `
	mutation GraphClawInferenceAppAddV3($input: GraphInferenceInferenceAppAddV3Input!) {
		graphInference_inferenceAppAddV3(input: $input) {
			success
			errors {
				code
				message
				field
				capabilityId
				reason
			}
			app {
				${getInferenceAppV3Fields(includePromptOverride)}
			}
		}
	}
`;

const INFERENCE_APP_EDIT_MUTATION = `
	mutation GraphClawInferenceAppEdit($input: GraphInferenceInferenceAppEditInput!) {
		graphInference_inferenceAppEdit(input: $input) {
			success
			errors {
				code
				message
			}
			app {
				id
				name
				product
				inferenceTarget
				inferenceType
				enabled
				realtime
			}
		}
	}
`;

const getInferenceAppEditV3Mutation = (includePromptOverride: boolean): string => `
	mutation GraphClawInferenceAppEditV3($input: GraphInferenceInferenceAppEditV3Input!) {
		graphInference_inferenceAppEditV3(input: $input) {
			success
			errors {
				code
				message
				field
				capabilityId
				reason
			}
			app {
				${getInferenceAppV3Fields(includePromptOverride)}
			}
		}
	}
`;

const INFERENCE_APP_DELETE_MUTATION = `
	mutation GraphClawInferenceAppDelete($input: GraphInferenceInferenceAppDeleteInput!) {
		graphInference_inferenceAppDelete(input: $input) {
			success
			errors {
				code
				message
			}
			app {
				id
			}
		}
	}
`;

const INFERENCE_APP_DELETE_V3_MUTATION = `
	mutation GraphClawInferenceAppDeleteV3($input: GraphInferenceInferenceAppDeleteV3Input!) {
		graphInference_inferenceAppDeleteV3(input: $input) {
			success
			errors {
				code
				message
				field
				capabilityId
				reason
			}
		}
	}
`;

type GraphQlError = {
	message?: string;
	extensions?: Record<string, unknown>;
};

type GraphQlResponse<TData> = {
	data?: TData;
	errors?: GraphQlError[];
};

type RequestHeaders = {
	Accept: string;
	'Content-Type': string;
	'X-Query-Context': string;
};

type GraphClawOptionResponse = {
	id?: unknown;
	name?: unknown;
	enabled?: unknown;
};

type GraphClawSourceOptionResponse = GraphClawOptionResponse & {
	matchers?: unknown;
};

type GraphClawInferDefinitionResponse = GraphClawOptionResponse & {
	resultKind?: unknown;
	targetRequirement?: unknown;
	targetUsage?: unknown;
	compatibleTargets?: unknown;
	compatibleSources?: unknown;
	supportedDestinations?: unknown;
	graphAvailable?: unknown;
	graphSources?: unknown;
};

type InferenceAppResponse = {
	id?: unknown;
	name?: unknown;
	product?: unknown;
	inferenceTarget?: unknown;
	inferenceType?: unknown;
	enabled?: unknown;
	realtime?: unknown;
};

type InferenceAppV3Response = {
	id?: unknown;
	owner?: unknown;
	name?: unknown;
	source?: unknown;
	matchers?: unknown;
	forTargets?: unknown;
	infer?: unknown;
	destination?: unknown;
	aiUseCaseId?: unknown;
	modelId?: unknown;
	reasoningLevel?: unknown;
	enabled?: unknown;
	realtime?: unknown;
	available?: unknown;
	promptOverride?: unknown;
};

type ExternallyManagedInferenceAppResponse = {
	id?: unknown;
	owner?: unknown;
	name?: unknown;
	source?: unknown;
	matchers?: unknown;
	forTargets?: unknown;
	infer?: unknown;
};

type GraphClawOptionsResponse = {
	products?: GraphClawOptionResponse[] | null;
	inferenceTargets?: GraphClawOptionResponse[] | null;
	inferenceTypes?: GraphClawInferDefinitionResponse[] | null;
};

type InferenceAppPermissionsResponse = {
	graphInference_inferenceAppPermissions?: {
		canView?: unknown;
		canManage?: unknown;
		canRunInference?: unknown;
	} | null;
};

type InferenceAppListResponse = {
	graphInference_inferenceAppList?: InferenceAppResponse[] | null;
};

type InferenceAppListV3Response = {
	graphInference_inferenceAppListV3?: InferenceAppV3Response[] | null;
};

type ExternallyManagedInferenceAppListResponse = {
	graphInference_externallyManagedAppList?: ExternallyManagedInferenceAppResponse[] | null;
};

type InferenceModelChoiceResponse = {
	id?: unknown;
	label?: unknown;
};

type InferenceModelsPayloadResponse = {
	useCaseId?: unknown;
	infer?: unknown;
	models?: unknown;
	runtimeSelectionSupported?: unknown;
};

type InferenceModelsResponse = {
	graphInference_inferenceModels?: InferenceModelsPayloadResponse | null;
};

type InferenceQualityMetricSummaryResponse = {
	taskRuns?: unknown;
	tasksSucceeded?: unknown;
	tasksFailed?: unknown;
	successRate?: unknown;
	sourceCountReportedRuns?: unknown;
	sourceResultCountSum?: unknown;
	inputEmptyRuns?: unknown;
	inputEmptyRate?: unknown;
	resultsGeneratedSum?: unknown;
	outputEmptyRuns?: unknown;
	outputEmptyRate?: unknown;
	claimMetricsReportedRuns?: unknown;
	claimsGeneratedSum?: unknown;
	claimsWithEvidenceSum?: unknown;
	claimsWithoutEvidenceSum?: unknown;
	evidenceCoverageRate?: unknown;
	schemaCheckedRuns?: unknown;
	schemaValidRuns?: unknown;
	schemaInvalidRuns?: unknown;
	schemaValidRate?: unknown;
	tokenUsageReportedRuns?: unknown;
	inputTokensSum?: unknown;
	outputTokensSum?: unknown;
	totalTokensSum?: unknown;
	thoughtTokensSum?: unknown;
	cachedReadTokensSum?: unknown;
	cachedWriteTokensSum?: unknown;
	contextTokensSum?: unknown;
	taskLatencyP50Ms?: unknown;
	taskLatencyP95Ms?: unknown;
	responseSizeP50Bytes?: unknown;
	responseSizeP95Bytes?: unknown;
	confidenceP50?: unknown;
	confidenceP95?: unknown;
};

type InferenceQualityMetricsResponse = {
	graphInference_inferenceQualityMetrics?: {
		apiVersion?: unknown;
		windowStart?: unknown;
		windowEnd?: unknown;
		resolution?: unknown;
		dataFreshThrough?: unknown;
		summary?: InferenceQualityMetricSummaryResponse | null;
		buckets?: Array<{
			bucketStart?: unknown;
			finalized?: unknown;
			dataFreshThrough?: unknown;
			metrics?: InferenceQualityMetricSummaryResponse | null;
		}> | null;
	} | null;
};

type InferenceAppStoredResultResponse = {
	inferenceNode?: {
		ari?: unknown;
		title?: unknown;
		description?: unknown;
		url?: unknown;
	} | null;
	linksEntities?: Array<{
		ari?: unknown;
		url?: unknown;
	}> | null;
	anchor?: {
		ari?: unknown;
		title?: unknown;
		description?: unknown;
		url?: unknown;
	} | null;
};

type InferenceAppStoredResultsResponse = {
	graphInference_inferenceAppStoredResults?: InferenceAppStoredResultResponse[] | null;
};

type InferredDecisionMetadataResponse = {
	__typename?: unknown;
	kind?: unknown;
	subtype?: unknown;
	confidenceScore?: unknown;
	createdAt?: unknown;
	decidedBy?: unknown;
};

type InferredDecisionNodeResponse = {
	id?: unknown;
	metadata?: InferredDecisionMetadataResponse | null;
};

type InferredDecisionHydrationResponse = {
	graphStoreV2?: {
		nodes?: Array<InferredDecisionNodeResponse | null> | null;
	} | null;
};

type MutationError = {
	code?: unknown;
	message?: unknown;
	field?: unknown;
	capabilityId?: unknown;
	reason?: unknown;
};

type MutationPayload<TApp> = {
	success?: boolean;
	errors?: MutationError[] | null;
	app?: TApp | null;
};

type InferenceAppAddResponse = {
	graphInference_inferenceAppAdd?: MutationPayload<InferenceAppResponse> | null;
};

type InferenceAppEditResponse = {
	graphInference_inferenceAppEdit?: MutationPayload<InferenceAppResponse> | null;
};

type InferenceAppDeleteResponse = {
	graphInference_inferenceAppDelete?: MutationPayload<{ id?: unknown }> | null;
};

type InferenceAppAddV3Response = {
	graphInference_inferenceAppAddV3?: MutationPayload<InferenceAppV3Response> | null;
};

type InferenceAppEditV3Response = {
	graphInference_inferenceAppEditV3?: MutationPayload<InferenceAppV3Response> | null;
};

type InferenceAppDeleteV3Response = {
	graphInference_inferenceAppDeleteV3?: MutationPayload<never> | null;
};

type InferenceAppAddInput = {
	product: string;
	inferenceTarget: string;
	inferenceType: string;
	name: string;
	enabled: boolean;
	realtime: boolean;
};

type InferenceAppEditInput = {
	id: InferenceAppId;
	name?: string;
	product?: string;
	inferenceTarget?: string;
	inferenceType?: string;
	enabled?: boolean;
	realtime?: boolean;
};

type InferenceAppAddV3Input = {
	createRequestId: string;
	name: string;
	source: string;
	matchers: string[];
	forTargets: string[];
	infer: string;
	destination: 'SHADOW' | 'GRAPH';
	ai: { useCaseId: string; model: { id: string; reasoningLevel: string } };
	enabled: boolean;
	realtime: boolean;
	promptOverride?: string;
};

type InferenceAppEditV3Input = {
	id: InferenceAppId;
	name?: string;
	source?: string;
	matchers?: string[];
	forTargets?: string[];
	infer?: string;
	destination?: 'SHADOW' | 'GRAPH';
	ai?: { useCaseId: string; model: { id: string; reasoningLevel: string } };
	enabled?: boolean;
	realtime?: boolean;
	promptOverride?: string | null;
};

export class GraphClawServiceError extends Error {
	readonly kind: GraphClawErrorKind;
	readonly field?: string;
	readonly capabilityId?: string;
	readonly reason?: string;

	constructor(
		message: string,
		{
			kind = 'unknown',
			field,
			capabilityId,
			reason,
		}: {
			kind?: GraphClawErrorKind;
			field?: string;
			capabilityId?: string;
			reason?: string;
		} = {},
	) {
		super(message);
		this.name = 'GraphClawServiceError';
		this.kind = kind;
		this.field = field;
		this.capabilityId = capabilityId;
		this.reason = reason;
	}
}

class GraphClawGraphQlError extends GraphClawServiceError {
	readonly graphQlErrors: GraphQlError[];

	constructor(graphQlErrors: GraphQlError[]) {
		const message = graphQlErrors
			.map((error) => error.message)
			.filter((errorMessage): errorMessage is string => Boolean(errorMessage))
			.join('; ');

		const code = graphQlErrors.find((error) => typeof error.extensions?.code === 'string')
			?.extensions?.code;
		super(message || 'GraphClaw GraphQL request failed.', {
			kind: getErrorKind(typeof code === 'string' ? code : undefined),
		});
		this.name = 'GraphClawGraphQlError';
		this.graphQlErrors = graphQlErrors;
	}
}

const getRequestHeaders = (): RequestHeaders => {
	const { defaultSiteCloudId } = getConfig();

	return {
		Accept: JSON_CONTENT_TYPE,
		'Content-Type': JSON_CONTENT_TYPE,
		'X-Query-Context': buildSiteAri(defaultSiteCloudId),
	};
};

export const requestGraphClaw = async <TData>({
	operationName,
	query,
	variables,
	allowPartialData = false,
	onPartialErrors,
}: {
	operationName: string;
	query: string;
	variables?: Record<string, unknown>;
	allowPartialData?: boolean;
	onPartialErrors?: (errors: GraphQlError[]) => void;
}): Promise<TData> => {
	const response = await fetch(getGraphQLEndpointUrl(), {
		method: 'POST',
		headers: getRequestHeaders(),
		body: JSON.stringify({
			operationName,
			query,
			variables,
		}),
	});

	if (!response.ok) {
		throw new GraphClawServiceError(`GraphClaw GraphQL request failed with ${response.status}.`, {
			kind: response.status === 401 || response.status === 403 ? 'permission' : 'dependency',
		});
	}

	const payload = (await response.json()) as GraphQlResponse<TData>;

	if (payload.errors?.length) {
		if (!(allowPartialData && payload.data)) {
			throw new GraphClawGraphQlError(payload.errors);
		}
		onPartialErrors?.(payload.errors);
	}

	if (!payload.data) {
		throw new GraphClawServiceError('GraphClaw GraphQL response did not include data.', {
			kind: 'dependency',
		});
	}

	return payload.data;
};

const requireString = (value: unknown, field: string): string => {
	if (typeof value !== 'string' || value.length === 0) {
		throw new GraphClawServiceError(`GraphClaw response missing ${field}.`, {
			kind: 'dependency',
		});
	}

	return value;
};

const requireBoolean = (value: unknown, field: string): boolean => {
	if (typeof value !== 'boolean') {
		throw new GraphClawServiceError(`GraphClaw response missing ${field}.`, {
			kind: 'dependency',
		});
	}

	return value;
};

const requireNumber = (value: unknown, field: string): number => {
	if (typeof value !== 'number' || !Number.isFinite(value)) {
		throw new GraphClawServiceError(`GraphClaw response missing ${field}.`, {
			kind: 'dependency',
		});
	}

	return value;
};

const nullableNumber = (value: unknown, field: string): number | null =>
	value == null ? null : requireNumber(value, field);

const optionalString = (value: unknown): string | undefined =>
	typeof value === 'string' && value.length > 0 ? value : undefined;

const requireNullableString = (value: unknown, field: string): string | null => {
	if (value === null || typeof value === 'string') {
		return value;
	}

	throw new GraphClawServiceError(`GraphClaw response missing ${field}.`, {
		kind: 'dependency',
	});
};

const requireNullableWebUrl = (value: unknown, field: string): string | null => {
	const url = requireNullableString(value, field);
	if (url === null) {
		return null;
	}

	const isSafeWebUrl = (() => {
		try {
			const protocol = new URL(url).protocol;
			return protocol === 'http:' || protocol === 'https:';
		} catch {
			return false;
		}
	})();
	if (isSafeWebUrl) {
		return url;
	}

	throw new GraphClawServiceError(`GraphClaw response contains an unsafe ${field}.`, {
		kind: 'dependency',
	});
};

const requireStringArray = (value: unknown, field: string): string[] => {
	if (
		!Array.isArray(value) ||
		value.some((item) => typeof item !== 'string' || item.length === 0)
	) {
		throw new GraphClawServiceError(`GraphClaw response missing ${field}.`, {
			kind: 'dependency',
		});
	}

	return value;
};

const getErrorKind = (code?: string): GraphClawErrorKind => {
	switch (code) {
		case 'BAD_USER_INPUT':
		case 'INVALID_APP':
			return 'validation';
		case 'CAPABILITY_UNAVAILABLE':
			return 'unavailable-capability';
		case 'DUPLICATE_APP':
			return 'duplicate';
		case 'REQUEST_CONFLICT':
			return 'request-conflict';
		case 'APP_NOT_FOUND':
			return 'not-found';
		case 'FORBIDDEN':
		case 'UNAUTHORIZED':
			return 'permission';
		case 'INTERNAL_SERVER_ERROR':
		case 'SERVICE_UNAVAILABLE':
			return 'dependency';
		default:
			return 'unknown';
	}
};

const titleLabel = (value: string | null | undefined): string =>
	(value ?? '')
		.split(/[-_\s]+/)
		.filter(Boolean)
		.map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
		.join(' ');

export const mapGraphClawOption = (option: unknown): GraphClawOption => {
	const responseOption = option as GraphClawOptionResponse;

	return {
		id: requireString(responseOption.id, 'id'),
		name: requireString(responseOption.name, 'name'),
		enabled: requireBoolean(responseOption.enabled, 'enabled'),
	};
};

export const mapGraphClawSourceOption = (option: unknown): GraphClawSourceOption => {
	const responseOption = option as GraphClawSourceOptionResponse;

	return {
		...mapGraphClawOption(option),
		matchers: Array.isArray(responseOption.matchers)
			? responseOption.matchers.map(mapGraphClawOption)
			: [],
	};
};

const requireCatalogValue = <TValue extends string>(
	value: unknown,
	field: string,
	allowedValues: readonly TValue[],
): TValue => {
	if (typeof value !== 'string' || !allowedValues.includes(value as TValue)) {
		throw new GraphClawServiceError(`GraphClaw response missing ${field}.`, {
			kind: 'dependency',
		});
	}

	return value as TValue;
};

export const mapGraphClawInferDefinition = (option: unknown): GraphClawInferDefinition => {
	const responseOption = option as GraphClawInferDefinitionResponse;
	const graphAvailable = responseOption.graphAvailable === true;
	const supportedDestinations = requireStringArray(
		responseOption.supportedDestinations,
		'supportedDestinations',
	).filter(
		(destination): destination is 'shadow' | 'graph' =>
			destination === 'shadow' || destination === 'graph',
	);
	if (graphAvailable && !supportedDestinations.includes('graph')) {
		supportedDestinations.push('graph');
	}

	return {
		...mapGraphClawOption(option),
		description: '',
		resultKind: requireCatalogValue(responseOption.resultKind, 'resultKind', [
			'entity',
			'relationship',
		]),
		targetRequirement: requireCatalogValue(responseOption.targetRequirement, 'targetRequirement', [
			'none',
			'optional',
			'required',
		]),
		targetUsage: requireCatalogValue(responseOption.targetUsage, 'targetUsage', [
			'none',
			'scope',
			'candidates',
		]),
		compatibleTargetIds: requireStringArray(responseOption.compatibleTargets, 'compatibleTargets'),
		compatibleSourceIds: requireStringArray(responseOption.compatibleSources, 'compatibleSources'),
		supportedDestinations,
		graphAvailable,
		...(Array.isArray(responseOption.graphSources)
			? { graphSources: requireStringArray(responseOption.graphSources, 'graphSources') }
			: {}),
	};
};

export const mapInferenceApp = (app: unknown): InferenceApp => {
	const responseApp = app as InferenceAppResponse;

	return {
		id: requireString(responseApp.id, 'id'),
		name: requireString(responseApp.name, 'name'),
		product: requireString(responseApp.product, 'product'),
		inferenceTarget: requireString(responseApp.inferenceTarget, 'inferenceTarget'),
		inferenceType: requireString(responseApp.inferenceType, 'inferenceType'),
		matchers: [],
		promptOverride: null,
		enabled: requireBoolean(responseApp.enabled, 'enabled'),
		realtime: responseApp.realtime === true,
		available: true,
	};
};

export const mapInferenceAppV3 = (app: unknown, includePromptOverride = false): InferenceApp => {
	const responseApp = app as InferenceAppV3Response;

	return {
		id: requireString(responseApp.id, 'id'),
		name: requireString(responseApp.name, 'name'),
		product: requireString(responseApp.source, 'source'),
		matchers: requireStringArray(responseApp.matchers, 'matchers'),
		inferenceTarget: requireStringArray(responseApp.forTargets, 'forTargets')[0] ?? '',
		inferenceType: requireString(responseApp.infer, 'infer'),
		promptOverride: includePromptOverride
			? requireNullableString(responseApp.promptOverride, 'promptOverride')
			: null,
		enabled: requireBoolean(responseApp.enabled, 'enabled'),
		realtime: requireBoolean(responseApp.realtime, 'realtime'),
		available: requireBoolean(responseApp.available, 'available'),
		v2Configuration: {
			name: requireString(responseApp.name, 'name'),
			source: requireString(responseApp.source, 'source'),
			infer: requireString(responseApp.infer, 'infer'),
			forTargets: requireStringArray(responseApp.forTargets, 'forTargets'),
			destination:
				requireString(responseApp.destination, 'destination').toLowerCase() === 'graph'
					? 'graph'
					: 'shadow',
			additionalPrompt: '',
			ai: {
				useCase: requireString(responseApp.aiUseCaseId, 'aiUseCaseId'),
				model: requireString(responseApp.modelId, 'modelId'),
				reasoningLevel: requireString(responseApp.reasoningLevel, 'reasoningLevel'),
			},
			enabled: requireBoolean(responseApp.enabled, 'enabled'),
			realtime: requireBoolean(responseApp.realtime, 'realtime'),
		},
	};
};

export const mapExternallyManagedInferenceApp = (app: unknown): ExternallyManagedInferenceApp => {
	const responseApp = app as ExternallyManagedInferenceAppResponse;

	return {
		id: requireString(responseApp.id, 'id'),
		owner: requireString(responseApp.owner, 'owner'),
		name: requireString(responseApp.name, 'name'),
		source: requireString(responseApp.source, 'source'),
		matchers: requireStringArray(responseApp.matchers, 'matchers'),
		forTargets: requireStringArray(responseApp.forTargets, 'forTargets'),
		infer: requireString(responseApp.infer, 'infer'),
	};
};

export const toGraphClawServiceError = (error: unknown): GraphClawServiceError =>
	error instanceof GraphClawServiceError
		? error
		: new GraphClawServiceError(
				error instanceof Error ? error.message : 'GraphClaw request failed.',
			);

const assertMutationSuccess = <TApp>(
	payload: MutationPayload<TApp> | null | undefined,
): TApp | null => {
	const errors = payload?.errors?.filter((error) => optionalString(error.message));
	if (!payload?.success || errors?.length) {
		const primaryError = errors?.[0];
		const code = optionalString(primaryError?.code);
		throw new GraphClawServiceError(
			errors
				?.map((error) => optionalString(error.message))
				.filter(Boolean)
				.join('; ') || 'GraphClaw mutation failed.',
			{
				kind: getErrorKind(code),
				field: optionalString(primaryError?.field),
				capabilityId: optionalString(primaryError?.capabilityId),
				reason: optionalString(primaryError?.reason),
			},
		);
	}

	return payload.app ?? null;
};

const assertMutationApp = <TApp>(payload: MutationPayload<TApp> | null | undefined): TApp => {
	const app = assertMutationSuccess(payload);
	if (!app) {
		throw new GraphClawServiceError('GraphClaw mutation response did not include app.', {
			kind: 'dependency',
		});
	}

	return app;
};

const toAddInput = (values: InferenceAppFormValues): InferenceAppAddInput => {
	if (!values.product || !values.inferenceTarget || !values.inferenceType) {
		throw new GraphClawServiceError(
			'An inference app requires a product, target, and inference type.',
		);
	}

	return {
		product: values.product,
		inferenceTarget: values.inferenceTarget,
		inferenceType: values.inferenceType,
		name: values.name.trim() || buildAutoTitle(values),
		enabled: values.enabled,
		realtime: values.realtime,
	};
};

const toAddV3Input = (
	values: InferenceAppFormValues,
	createRequestId: string,
	includePromptOverride: boolean,
): InferenceAppAddV3Input => {
	if (
		values.name.trim().length === 0 ||
		!values.product ||
		!values.inferenceType ||
		values.matchers.length === 0 ||
		!values.v2Configuration
	) {
		throw new GraphClawServiceError(
			'An inference app requires a name, source, matcher, Infer definition, and AI configuration.',
			{
				kind: 'validation',
				field:
					values.name.trim().length === 0
						? 'name'
						: values.matchers.length === 0
							? 'matchers'
							: undefined,
			},
		);
	}

	return {
		createRequestId,
		name: values.name.trim(),
		source: values.product,
		matchers: values.matchers,
		forTargets: values.v2Configuration.forTargets,
		infer: values.inferenceType,
		destination: values.v2Configuration.destination === 'graph' ? 'GRAPH' : 'SHADOW',
		ai: {
			useCaseId: values.v2Configuration.ai.useCase,
			model: {
				id: values.v2Configuration.ai.model,
				reasoningLevel: values.v2Configuration.ai.reasoningLevel,
			},
		},
		enabled: values.enabled,
		realtime: values.realtime,
		...(includePromptOverride && values.promptOverride
			? { promptOverride: values.promptOverride }
			: {}),
	};
};

type AutoTitleValues = Pick<
	InferenceAppFormValues,
	'product' | 'inferenceTarget' | 'inferenceType'
>;

const getEditedName = (values: InferenceAppEditValues): string | undefined => {
	if (values.name === undefined) {
		return undefined;
	}

	return (
		values.name.trim() ||
		buildAutoTitle({
			product: values.product ?? null,
			inferenceTarget: values.inferenceTarget ?? null,
			inferenceType: values.inferenceType ?? null,
		})
	);
};

const toEditInput = (
	id: InferenceAppId,
	values: InferenceAppEditValues,
): InferenceAppEditInput => ({
	id,
	name: getEditedName(values),
	product: values.product,
	inferenceTarget: values.inferenceTarget,
	inferenceType: values.inferenceType,
	enabled: values.enabled,
	realtime: values.realtime,
});

const toEditV3Input = (
	id: InferenceAppId,
	values: InferenceAppEditValues,
	includePromptOverride: boolean,
): InferenceAppEditV3Input => {
	if (values.name !== undefined && values.name.trim().length === 0) {
		throw new GraphClawServiceError('An inference app requires a name.', {
			kind: 'validation',
			field: 'name',
		});
	}

	if (values.matchers?.length === 0) {
		throw new GraphClawServiceError('Select at least one matcher.', {
			kind: 'validation',
			field: 'matchers',
		});
	}

	return {
		id,
		name: values.name?.trim(),
		source: values.product,
		matchers: values.matchers,
		forTargets: values.v2Configuration?.forTargets,
		infer: values.inferenceType,
		destination: values.v2Configuration
			? values.v2Configuration.destination === 'graph'
				? 'GRAPH'
				: 'SHADOW'
			: undefined,
		ai: values.v2Configuration
			? {
					useCaseId: values.v2Configuration.ai.useCase,
					model: {
						id: values.v2Configuration.ai.model,
						reasoningLevel: values.v2Configuration.ai.reasoningLevel,
					},
				}
			: undefined,
		enabled: values.enabled,
		realtime: values.realtime,
		...(includePromptOverride && values.promptOverride !== undefined
			? { promptOverride: values.promptOverride }
			: {}),
	};
};

export const buildAutoTitle = (values: AutoTitleValues): string => {
	const inferenceType = titleLabel(values.inferenceType);
	const inferenceTarget = titleLabel(values.inferenceTarget);
	const product = titleLabel(values.product);

	const parts: string[] = [];
	if (inferenceType) {
		parts.push(`Infer ${inferenceType}`);
	}
	if (inferenceTarget) {
		parts.push(`for ${inferenceTarget}`);
	}
	if (product) {
		parts.push(`with ${product}`);
	}

	return parts.join(' ').trim() || 'Untitled inference app';
};

export const fetchGraphClawOptions = async (
	apiVersion: GraphClawApiVersion = 'v1',
	source?: string | null,
): Promise<GraphClawOptions> => {
	const data = await requestGraphClaw<GraphClawOptionsResponse>({
		operationName: apiVersion === 'v2' ? 'GraphClawOptionsV3' : 'GraphClawOptions',
		query: apiVersion === 'v2' ? GRAPHCLAW_OPTIONS_V3_QUERY : GRAPHCLAW_OPTIONS_QUERY,
		variables: apiVersion === 'v2' ? { source: source ?? null } : undefined,
	});

	const products = (data.products ?? []).map(mapGraphClawSourceOption);
	const inferenceTypes = (data.inferenceTypes ?? []).map(mapGraphClawOption);

	return {
		products,
		inferenceTargets: (data.inferenceTargets ?? []).map(mapGraphClawOption),
		inferenceTypes,
		inferDefinitions:
			apiVersion === 'v2'
				? (data.inferenceTypes ?? []).map(mapGraphClawInferDefinition)
				: undefined,
	};
};

export const fetchGraphClawInferenceAppPermissions =
	async (): Promise<GraphClawInferenceAppPermissions> => {
		const { defaultSiteCloudId } = getConfig();
		const data = await requestGraphClaw<InferenceAppPermissionsResponse>({
			operationName: 'GraphClawInferenceAppPermissions',
			query: INFERENCE_APP_PERMISSIONS_QUERY,
			variables: { cloudId: defaultSiteCloudId },
		});
		const permissions = data.graphInference_inferenceAppPermissions;

		if (!permissions) {
			throw new GraphClawServiceError('GraphClaw response missing inference app permissions.', {
				kind: 'dependency',
			});
		}

		return {
			canView: requireBoolean(permissions.canView, 'permissions.canView'),
			canManage: requireBoolean(permissions.canManage, 'permissions.canManage'),
			canRunInference: requireBoolean(permissions.canRunInference, 'permissions.canRunInference'),
		};
	};

export const fetchInferenceApps = async (
	apiVersion: GraphClawApiVersion = 'v1',
	includePromptOverride = false,
): Promise<InferenceApp[]> => {
	if (apiVersion === 'v2') {
		const data = await requestGraphClaw<InferenceAppListV3Response>({
			operationName: 'GraphClawInferenceAppListV3',
			query: getInferenceAppListV3Query(includePromptOverride),
		});

		return (data.graphInference_inferenceAppListV3 ?? []).map((app) =>
			mapInferenceAppV3(app, includePromptOverride),
		);
	}

	const data = await requestGraphClaw<InferenceAppListResponse>({
		operationName: 'GraphClawInferenceAppList',
		query: INFERENCE_APP_LIST_QUERY,
	});

	return (data.graphInference_inferenceAppList ?? []).map(mapInferenceApp);
};

export const fetchExternallyManagedInferenceApps = async (): Promise<
	ExternallyManagedInferenceApp[]
> => {
	const data = await requestGraphClaw<ExternallyManagedInferenceAppListResponse>({
		operationName: 'GraphClawExternallyManagedAppList',
		query: EXTERNALLY_MANAGED_APP_LIST_QUERY,
	});

	return (data.graphInference_externallyManagedAppList ?? []).map(mapExternallyManagedInferenceApp);
};

export const fetchGraphClawHubData = async (
	apiVersion: GraphClawApiVersion = 'v1',
	source?: string | null,
	permissionsOverride?: GraphClawInferenceAppPermissions,
): Promise<{
	permissions: GraphClawInferenceAppPermissions;
	options: GraphClawOptions;
	apps: InferenceApp[];
}> => {
	const permissions = permissionsOverride ?? (await fetchGraphClawInferenceAppPermissions());
	if (!permissions.canView) {
		return { permissions, options: emptyGraphClawOptions, apps: [] };
	}

	const [options, apps] = await Promise.all([
		fetchGraphClawOptions(apiVersion, source),
		fetchInferenceApps(apiVersion, apiVersion === 'v2' && permissions.canManage),
	]);

	return { permissions, options, apps };
};

const parseInferenceQualityMetricValues = (
	metrics: InferenceQualityMetricSummaryResponse,
	fieldPrefix: string,
): GraphClawInferenceQualityMetricValues => ({
	taskRuns: nullableNumber(metrics.taskRuns, `${fieldPrefix}.taskRuns`),
	tasksSucceeded: nullableNumber(metrics.tasksSucceeded, `${fieldPrefix}.tasksSucceeded`),
	tasksFailed: nullableNumber(metrics.tasksFailed, `${fieldPrefix}.tasksFailed`),
	successRate: nullableNumber(metrics.successRate, `${fieldPrefix}.successRate`),
	sourceCountReportedRuns: nullableNumber(
		metrics.sourceCountReportedRuns,
		`${fieldPrefix}.sourceCountReportedRuns`,
	),
	sourceResultCountSum: nullableNumber(
		metrics.sourceResultCountSum,
		`${fieldPrefix}.sourceResultCountSum`,
	),
	inputEmptyRuns: nullableNumber(metrics.inputEmptyRuns, `${fieldPrefix}.inputEmptyRuns`),
	inputEmptyRate: nullableNumber(metrics.inputEmptyRate, `${fieldPrefix}.inputEmptyRate`),
	resultsGeneratedSum: nullableNumber(
		metrics.resultsGeneratedSum,
		`${fieldPrefix}.resultsGeneratedSum`,
	),
	outputEmptyRuns: nullableNumber(metrics.outputEmptyRuns, `${fieldPrefix}.outputEmptyRuns`),
	outputEmptyRate: nullableNumber(metrics.outputEmptyRate, `${fieldPrefix}.outputEmptyRate`),
	claimMetricsReportedRuns: nullableNumber(
		metrics.claimMetricsReportedRuns,
		`${fieldPrefix}.claimMetricsReportedRuns`,
	),
	claimsGeneratedSum: nullableNumber(
		metrics.claimsGeneratedSum,
		`${fieldPrefix}.claimsGeneratedSum`,
	),
	claimsWithEvidenceSum: nullableNumber(
		metrics.claimsWithEvidenceSum,
		`${fieldPrefix}.claimsWithEvidenceSum`,
	),
	claimsWithoutEvidenceSum: nullableNumber(
		metrics.claimsWithoutEvidenceSum,
		`${fieldPrefix}.claimsWithoutEvidenceSum`,
	),
	evidenceCoverageRate: nullableNumber(
		metrics.evidenceCoverageRate,
		`${fieldPrefix}.evidenceCoverageRate`,
	),
	schemaCheckedRuns: nullableNumber(metrics.schemaCheckedRuns, `${fieldPrefix}.schemaCheckedRuns`),
	schemaValidRuns: nullableNumber(metrics.schemaValidRuns, `${fieldPrefix}.schemaValidRuns`),
	schemaInvalidRuns: nullableNumber(metrics.schemaInvalidRuns, `${fieldPrefix}.schemaInvalidRuns`),
	schemaValidRate: nullableNumber(metrics.schemaValidRate, `${fieldPrefix}.schemaValidRate`),
	tokenUsageReportedRuns: nullableNumber(
		metrics.tokenUsageReportedRuns,
		`${fieldPrefix}.tokenUsageReportedRuns`,
	),
	inputTokensSum: nullableNumber(metrics.inputTokensSum, `${fieldPrefix}.inputTokensSum`),
	outputTokensSum: nullableNumber(metrics.outputTokensSum, `${fieldPrefix}.outputTokensSum`),
	totalTokensSum: nullableNumber(metrics.totalTokensSum, `${fieldPrefix}.totalTokensSum`),
	thoughtTokensSum: nullableNumber(metrics.thoughtTokensSum, `${fieldPrefix}.thoughtTokensSum`),
	cachedReadTokensSum: nullableNumber(
		metrics.cachedReadTokensSum,
		`${fieldPrefix}.cachedReadTokensSum`,
	),
	cachedWriteTokensSum: nullableNumber(
		metrics.cachedWriteTokensSum,
		`${fieldPrefix}.cachedWriteTokensSum`,
	),
	contextTokensSum: nullableNumber(metrics.contextTokensSum, `${fieldPrefix}.contextTokensSum`),
	taskLatencyP50Ms: nullableNumber(metrics.taskLatencyP50Ms, `${fieldPrefix}.taskLatencyP50Ms`),
	taskLatencyP95Ms: nullableNumber(metrics.taskLatencyP95Ms, `${fieldPrefix}.taskLatencyP95Ms`),
	responseSizeP50Bytes: nullableNumber(
		metrics.responseSizeP50Bytes,
		`${fieldPrefix}.responseSizeP50Bytes`,
	),
	responseSizeP95Bytes: nullableNumber(
		metrics.responseSizeP95Bytes,
		`${fieldPrefix}.responseSizeP95Bytes`,
	),
	confidenceP50: nullableNumber(metrics.confidenceP50, `${fieldPrefix}.confidenceP50`),
	confidenceP95: nullableNumber(metrics.confidenceP95, `${fieldPrefix}.confidenceP95`),
});

export const fetchInferenceQualityMetrics = async (
	appId: InferenceAppId,
	lookbackHours: number,
	resolution: 'HOUR' | 'DAY',
	executionStatus?: 'SUCCEEDED' | 'FAILED',
): Promise<GraphClawInferenceQualityMetrics> => {
	const data = await requestGraphClaw<InferenceQualityMetricsResponse>({
		operationName: 'GraphClawInferenceQualityMetrics',
		query: INFERENCE_QUALITY_METRICS_QUERY,
		variables: {
			input: {
				appId,
				lookbackHours,
				resolution,
				...(executionStatus ? { executionStatus } : {}),
			},
		},
	});
	const response = data.graphInference_inferenceQualityMetrics;
	const summary = response?.summary;
	if (!summary) {
		throw new GraphClawServiceError('GraphClaw response missing inference metrics.', {
			kind: 'dependency',
		});
	}

	return {
		apiVersion: typeof response?.apiVersion === 'string' ? response.apiVersion : null,
		windowStart: typeof response?.windowStart === 'string' ? response.windowStart : null,
		windowEnd: typeof response?.windowEnd === 'string' ? response.windowEnd : null,
		resolution: typeof response?.resolution === 'string' ? response.resolution : null,
		dataFreshThrough:
			typeof response?.dataFreshThrough === 'string' ? response.dataFreshThrough : null,
		summary: parseInferenceQualityMetricValues(summary, 'metrics.summary'),
		buckets: (response?.buckets ?? []).map((bucket, index) => ({
			bucketStart: typeof bucket.bucketStart === 'string' ? bucket.bucketStart : null,
			finalized: typeof bucket.finalized === 'boolean' ? bucket.finalized : null,
			dataFreshThrough:
				typeof bucket.dataFreshThrough === 'string' ? bucket.dataFreshThrough : null,
			metrics: bucket.metrics
				? parseInferenceQualityMetricValues(bucket.metrics, `metrics.buckets.${index}`)
				: null,
		})),
	};
};

const mapInferenceAppStoredResult = (
	result: InferenceAppStoredResultResponse,
	index: number,
): GraphClawStoredResult => {
	const inferenceNode = result.inferenceNode;
	if (!inferenceNode) {
		throw new GraphClawServiceError(
			`GraphClaw response missing storedResults.${index}.inferenceNode.`,
			{ kind: 'dependency' },
		);
	}
	if (!Array.isArray(result.linksEntities)) {
		throw new GraphClawServiceError(
			`GraphClaw response missing storedResults.${index}.linksEntities.`,
			{ kind: 'dependency' },
		);
	}

	const inferenceNodeAri = requireString(
		inferenceNode.ari,
		`storedResults.${index}.inferenceNode.ari`,
	);
	let inferenceNodeEntityType: string | null = null;
	try {
		inferenceNodeEntityType = AnyAri.parse(inferenceNodeAri).resourceType;
	} catch {
		// Keep unknown entity types on the established decision layout.
	}

	return {
		inferenceNode: {
			ari: inferenceNodeAri,
			entityType: inferenceNodeEntityType,
			title: requireString(inferenceNode.title, `storedResults.${index}.inferenceNode.title`),
			description: requireNullableString(
				inferenceNode.description,
				`storedResults.${index}.inferenceNode.description`,
			),
			url:
				inferenceNode.url === undefined
					? null
					: requireNullableWebUrl(inferenceNode.url, `storedResults.${index}.inferenceNode.url`),
		},
		linksEntities: result.linksEntities.map((entity, entityIndex) => ({
			ari: requireString(entity.ari, `storedResults.${index}.linksEntities.${entityIndex}.ari`),
			url: requireNullableWebUrl(
				entity.url,
				`storedResults.${index}.linksEntities.${entityIndex}.url`,
			),
		})),
		anchor: result.anchor
			? {
					ari: requireString(result.anchor.ari, `storedResults.${index}.anchor.ari`),
					title: requireNullableString(result.anchor.title, `storedResults.${index}.anchor.title`),
					description: requireNullableString(
						result.anchor.description,
						`storedResults.${index}.anchor.description`,
					),
					url: requireNullableWebUrl(result.anchor.url, `storedResults.${index}.anchor.url`),
				}
			: null,
	};
};

const optionalMetadataString = (value: unknown): string | null =>
	typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;

const optionalMetadataNumber = (value: unknown): number | null => {
	if (typeof value === 'number') return Number.isFinite(value) ? value : null;
	if (typeof value === 'string' && value.trim().length > 0) {
		const parsed = Number(value);
		return Number.isFinite(parsed) ? parsed : null;
	}
	return null;
};

const parseDecidedBy = (value: unknown): string[] => {
	const deciders: string[] = [];
	const add = (candidate: string) => {
		const normalized = candidate.trim().replace(/^['"]|['"]$/g, '');
		if (normalized && !deciders.includes(normalized)) deciders.push(normalized);
	};
	const collect = (candidate: unknown): void => {
		if (Array.isArray(candidate)) return candidate.forEach(collect);
		if (candidate && typeof candidate === 'object') {
			const wrappedValue = (candidate as { value?: unknown }).value;
			if (typeof wrappedValue === 'string') add(wrappedValue);
			return;
		}
		if (typeof candidate !== 'string' || candidate.trim().length === 0) return;
		const serialized = candidate.trim();
		try {
			const parsed = JSON.parse(serialized) as unknown;
			if (parsed !== serialized) return collect(parsed);
		} catch {
			// Graph Store can serialize wrapped strings as `[{value=Saurabh}]`, which is not JSON.
		}
		if (serialized.includes('value=')) {
			serialized
				.split('value=')
				.slice(1)
				.forEach((segment) => add(segment.split('}')[0] ?? ''));
		} else if (!serialized.startsWith('[') && !serialized.startsWith('{')) {
			add(serialized);
		}
	};
	collect(value);
	return deciders;
};

const parseInferredDecisionMetadata = (
	metadata: InferredDecisionMetadataResponse | null | undefined,
): GraphClawStoredDecisionMetadata | null => {
	if (metadata?.__typename !== 'GraphStoreInferredDecisionNodeMetadata') return null;
	const confidenceScore = optionalMetadataNumber(metadata.confidenceScore);
	return {
		kind: optionalMetadataString(metadata.kind),
		subtype: optionalMetadataString(metadata.subtype),
		confidenceScore:
			confidenceScore !== null && confidenceScore >= 0 && confidenceScore <= 1
				? confidenceScore
				: null,
		createdAt: optionalMetadataNumber(metadata.createdAt),
		decidedBy: parseDecidedBy(metadata.decidedBy),
	};
};

const hydrateInferredDecisionMetadata = async (
	ids: string[],
): Promise<Map<string, GraphClawStoredDecisionMetadata>> => {
	const metadataByAri = new Map<string, GraphClawStoredDecisionMetadata>();
	const batches = Array.from(
		{ length: Math.ceil(ids.length / GRAPH_STORE_NODE_BATCH_SIZE) },
		(_, index) =>
			ids.slice(index * GRAPH_STORE_NODE_BATCH_SIZE, (index + 1) * GRAPH_STORE_NODE_BATCH_SIZE),
	);
	for (let index = 0; index < batches.length; index += GRAPH_STORE_HYDRATION_CONCURRENCY) {
		const responses = await Promise.all(
			batches.slice(index, index + GRAPH_STORE_HYDRATION_CONCURRENCY).map((batch) =>
				requestGraphClaw<InferredDecisionHydrationResponse>({
					operationName: 'GraphClawHydrateInferredDecisionMetadata',
					query: INFERRED_DECISION_METADATA_QUERY,
					variables: { ids: batch },
				}),
			),
		);
		for (const response of responses) {
			const nodes = response.graphStoreV2?.nodes;
			if (!Array.isArray(nodes)) {
				throw new GraphClawServiceError('Graph Store response missing hydrated decision nodes.', {
					kind: 'dependency',
				});
			}
			for (const node of nodes) {
				const ari = optionalMetadataString(node?.id);
				const metadata = parseInferredDecisionMetadata(node?.metadata);
				if (ari && metadata) metadataByAri.set(ari, metadata);
			}
		}
	}
	return metadataByAri;
};

const hydrateInferredDecisionMetadataWithinTimeout = (ids: string[]) => {
	let timeoutId: ReturnType<typeof setTimeout>;
	const timeout = new Promise<never>((_, reject) => {
		timeoutId = setTimeout(
			() => reject(new Error('Graph Store metadata timed out.')),
			METADATA_HYDRATION_TIMEOUT_MS,
		);
	});
	return Promise.race([hydrateInferredDecisionMetadata(ids), timeout]).finally(() =>
		clearTimeout(timeoutId),
	);
};

export const fetchInferenceAppStoredResults = async (
	appId: InferenceAppId,
): Promise<GraphClawStoredResult[]> => {
	const data = await requestGraphClaw<InferenceAppStoredResultsResponse>({
		operationName: 'GraphClawInferenceAppStoredResults',
		query: INFERENCE_APP_STORED_RESULTS_QUERY,
		variables: { input: { appId } },
	});
	const results = data.graphInference_inferenceAppStoredResults;
	if (!Array.isArray(results)) {
		throw new GraphClawServiceError('GraphClaw response missing stored results.', {
			kind: 'dependency',
		});
	}

	const storedResults = results.map(mapInferenceAppStoredResult);
	const inferredDecisionIds = Array.from(
		new Set(
			storedResults
				.filter((result) => result.inferenceNode.entityType === 'inferred-decision')
				.map((result) => result.inferenceNode.ari),
		),
	);
	if (inferredDecisionIds.length === 0) return storedResults;
	const metadataByAri = await hydrateInferredDecisionMetadataWithinTimeout(
		inferredDecisionIds,
	).catch(() => new Map<string, GraphClawStoredDecisionMetadata>());
	return storedResults.map((result) => {
		const metadata = metadataByAri.get(result.inferenceNode.ari);
		return metadata ? { ...result, inferenceNode: { ...result.inferenceNode, metadata } } : result;
	});
};

const fetchInferenceResultsForType = async (
	type: Exclude<InferenceResultType, 'all'>,
	page: number,
	includeExpandedRelationships: boolean,
) => {
	const offset = (Math.max(1, page) - 1) * INFERENCE_RESULTS_PAGE_SIZE;
	let isPartial = false;
	const cypherQuery =
		includeExpandedRelationships && type !== 'artifact'
			? EXPANDED_INFERENCE_CYPHER_QUERIES[type](offset)
			: INFERENCE_CYPHER_QUERIES[type](offset);
	const data = await requestGraphClaw<NonNullable<InferenceQueryResponse['data']>>({
		operationName: 'GraphClawInferenceResults',
		query: INFERENCE_RESULTS_QUERY,
		allowPartialData: true,
		onPartialErrors: () => {
			isPartial = true;
		},
		variables: {
			cypherQuery,
			params: { offset },
		},
	});
	if (!data.graphStore?.cypherQueryV2) {
		throw new GraphClawServiceError('Graph Store did not return inference results.', {
			kind: 'dependency',
		});
	}

	return {
		...parseInferenceQueryResponse({ data }, type),
		...(isPartial ? { isPartial: true } : {}),
	};
};

const fetchAllInferenceResults = async (page: number): Promise<InferenceResultsPage> => {
	const normalizedPage = Math.max(1, page);
	const offset = (normalizedPage - 1) * INFERENCE_RESULTS_PAGE_SIZE;
	const prefixLimit = normalizedPage * INFERENCE_RESULTS_PAGE_SIZE;
	let isPartial = false;
	type InferenceConnection = NonNullable<
		NonNullable<NonNullable<InferenceQueryResponse['data']>['graphStore']>['cypherQueryV2']
	>;
	const data = await requestGraphClaw<{
		graphStore?: {
			projects?: InferenceConnection;
			decisions?: InferenceConnection;
			teams?: InferenceConnection;
			artifacts?: InferenceConnection;
		};
	}>({
		operationName: 'GraphClawAllInferenceResults',
		query: ALL_INFERENCE_RESULTS_QUERY,
		allowPartialData: true,
		onPartialErrors: () => {
			isPartial = true;
		},
		variables: {
			projectQuery: EXPANDED_INFERENCE_CYPHER_QUERIES.project(0, prefixLimit),
			decisionQuery: EXPANDED_INFERENCE_CYPHER_QUERIES.decision(0, prefixLimit),
			teamQuery: EXPANDED_INFERENCE_CYPHER_QUERIES.team(0, prefixLimit),
			artifactQuery: INFERENCE_CYPHER_QUERIES.artifact(0, prefixLimit),
		},
	});
	if (!data.graphStore) {
		throw new GraphClawServiceError('Graph Store did not return inference results.', {
			kind: 'dependency',
		});
	}

	const pages = (
		[
			['project', data.graphStore.projects],
			['decision', data.graphStore.decisions],
			['team', data.graphStore.teams],
			['artifact', data.graphStore.artifacts],
		] as const
	).map(([type, cypherQueryV2]) =>
		parseInferenceQueryResponse({ data: { graphStore: { cypherQueryV2 } } }, type),
	);
	const mergedRecords = mergeInferenceRecords(pages);
	return {
		records: mergedRecords.slice(offset, offset + INFERENCE_RESULTS_PAGE_SIZE),
		hasNextPage:
			mergedRecords.length > offset + INFERENCE_RESULTS_PAGE_SIZE ||
			pages.some((result) => result.records.length >= prefixLimit),
		...(isPartial ? { isPartial: true } : {}),
	};
};

export const fetchInferenceResults = async ({
	type = 'all',
	page = 1,
	includeExpandedRelationships = false,
}: {
	type?: InferenceResultType;
	page?: number;
	includeExpandedRelationships?: boolean;
} = {}): Promise<InferenceResultsPage> => {
	if (type === 'all') {
		return fetchAllInferenceResults(page);
	}

	const result = await fetchInferenceResultsForType(type, page, includeExpandedRelationships);
	return {
		records: result.records,
		hasNextPage: result.records.length >= INFERENCE_RESULTS_PAGE_SIZE,
		...(result.isPartial ? { isPartial: true } : {}),
	};
};

export type InferenceDetailResult = {
	record: InferenceRecord | null;
	isPartial: boolean;
};

export const fetchInferenceDetail = async (
	location: ExplorerLocation,
): Promise<InferenceDetailResult> => {
	if (!location.node) {
		return { record: null, isPartial: false };
	}
	// The list filter is not the selected node's type. Avoid querying unrelated
	// inference types (and artifact relationships) for a known inferred-node ARI.
	const inferredType = location.node.match(
		/^ari:cloud:graph-store::inferred-(project|decision|team)\//,
	)?.[1] as 'project' | 'decision' | 'team' | undefined;
	const types: Array<Exclude<InferenceResultType, 'all'>> =
		location.type !== 'all'
			? [location.type]
			: location.source || location.row
				? ['artifact']
				: inferredType
					? [inferredType]
					: ['project', 'decision', 'team', 'artifact'];
	const results = await Promise.allSettled(
		types.map(async (type) => {
			let isPartial = false;
			const data = await requestGraphClaw<NonNullable<InferenceQueryResponse['data']>>({
				operationName: 'GraphClawInferenceResults',
				query: INFERENCE_RESULTS_QUERY,
				allowPartialData: true,
				onPartialErrors: () => {
					isPartial = true;
				},
				variables: {
					cypherQuery: inferenceDetailQuery(type),
					params: { node: location.node, source: location.source ?? null },
				},
			});
			if (!data.graphStore?.cypherQueryV2) {
				throw new GraphClawServiceError('Graph Store did not return the selected inference.', {
					kind: 'dependency',
				});
			}
			const parsed = parseInferenceQueryResponse({ data }, type);
			return {
				...parsed,
				isPartial: isPartial || (data.graphStore.cypherQueryV2.edges?.length ?? 0) >= 1000,
			};
		}),
	);
	for (const result of results) {
		if (result.status === 'fulfilled') {
			const record = result.value.records.find((record) =>
				matchesExplorerLocation(record, location),
			);
			if (record) {
				return { record, isPartial: result.value.isPartial };
			}
		}
	}
	// Only report a missing node when every candidate query completed successfully.
	const failure = results.find((result) => result.status === 'rejected');
	if (failure?.status === 'rejected') {
		throw failure.reason;
	}
	return {
		record: null,
		isPartial: results.some((result) => result.status === 'fulfilled' && result.value.isPartial),
	};
};

export const lookupApprovedModels = async (
	useCaseId: string,
	infer: string,
): Promise<GraphClawApprovedModelLookupResponse> => {
	const data = await requestGraphClaw<InferenceModelsResponse>({
		operationName: 'GraphClawInferenceModels',
		query: INFERENCE_MODELS_QUERY,
		variables: { input: { useCaseId, infer } },
	});
	const response = data.graphInference_inferenceModels;
	if (!response || !Array.isArray(response.models)) {
		throw new GraphClawServiceError('GraphClaw response missing inference models.', {
			kind: 'dependency',
		});
	}

	return {
		use_case_id: requireString(response.useCaseId, 'useCaseId'),
		infer: requireString(response.infer, 'infer'),
		models: response.models.map((model) => {
			const responseModel = model as InferenceModelChoiceResponse;
			return {
				id: requireString(responseModel.id, 'models.id'),
				label: requireString(responseModel.label, 'models.label'),
			};
		}),
		runtime_selection_supported: requireBoolean(
			response.runtimeSelectionSupported,
			'runtimeSelectionSupported',
		),
	};
};

export const createInferenceApp = async (
	values: InferenceAppFormValues,
	apiVersion: GraphClawApiVersion = 'v1',
	createRequestId?: string,
	includePromptOverride = false,
): Promise<InferenceApp> => {
	if (apiVersion === 'v2') {
		if (!createRequestId) {
			throw new GraphClawServiceError('A create request ID is required.', {
				kind: 'validation',
				field: 'createRequestId',
			});
		}

		const input = toAddV3Input(values, createRequestId, includePromptOverride);
		const data = await requestGraphClaw<InferenceAppAddV3Response>({
			operationName: 'GraphClawInferenceAppAddV3',
			query: getInferenceAppAddV3Mutation(includePromptOverride),
			variables: { input },
		});

		return mapInferenceAppV3(
			assertMutationApp(data.graphInference_inferenceAppAddV3),
			includePromptOverride,
		);
	}

	const input = toAddInput(values);
	const data = await requestGraphClaw<InferenceAppAddResponse>({
		operationName: 'GraphClawInferenceAppAdd',
		query: INFERENCE_APP_ADD_MUTATION,
		variables: { input },
	});

	return mapInferenceApp(assertMutationApp(data.graphInference_inferenceAppAdd));
};

export const updateInferenceApp = async (
	id: InferenceAppId,
	values: InferenceAppEditValues,
	apiVersion: GraphClawApiVersion = 'v1',
	includePromptOverride = false,
): Promise<InferenceApp> => {
	if (apiVersion === 'v2') {
		const input = toEditV3Input(id, values, includePromptOverride);
		const data = await requestGraphClaw<InferenceAppEditV3Response>({
			operationName: 'GraphClawInferenceAppEditV3',
			query: getInferenceAppEditV3Mutation(includePromptOverride),
			variables: { input },
		});

		return mapInferenceAppV3(
			assertMutationApp(data.graphInference_inferenceAppEditV3),
			includePromptOverride,
		);
	}

	const input = toEditInput(id, values);
	const data = await requestGraphClaw<InferenceAppEditResponse>({
		operationName: 'GraphClawInferenceAppEdit',
		query: INFERENCE_APP_EDIT_MUTATION,
		variables: { input },
	});

	return mapInferenceApp(assertMutationApp(data.graphInference_inferenceAppEdit));
};

export const deleteInferenceApp = async (
	id: InferenceAppId,
	apiVersion: GraphClawApiVersion = 'v1',
): Promise<InferenceAppId> => {
	if (apiVersion === 'v2') {
		const data = await requestGraphClaw<InferenceAppDeleteV3Response>({
			operationName: 'GraphClawInferenceAppDeleteV3',
			query: INFERENCE_APP_DELETE_V3_MUTATION,
			variables: { input: { id } },
		});
		assertMutationSuccess(data.graphInference_inferenceAppDeleteV3);

		return id;
	}

	const data = await requestGraphClaw<InferenceAppDeleteResponse>({
		operationName: 'GraphClawInferenceAppDelete',
		query: INFERENCE_APP_DELETE_MUTATION,
		variables: { input: { id } },
	});
	const deletedApp = assertMutationSuccess(data.graphInference_inferenceAppDelete);

	return deletedApp?.id ? requireString(deletedApp.id, 'id') : id;
};
